/**
 * @ngdoc service
 * @module flowingly.services
 * @name flowModelNodeSequenceService
 *
 * @description A service for calculating model nodes sequence
 */

import IModelerNode from '@Shared.Angular/@types/modelerNode';
import { IModelerNodeLink } from '@Shared.Angular/@types/workflow';
import { Services } from '@Shared.Angular/@types/services';
import { NodeCategory } from '@Shared.Angular/flowingly.services/flowingly.constants';
import angular from 'angular';

type NodeLike =
  | IModelerNode
  | { id: string; key: number; refSequence?: string; category?: NodeCategory };

type QueueItem = {
  node: NodeLike;
  refLetter: string;
  refNum: number;
};

export default class FlowModelNodeSequenceService {
  public static $inject = ['pubsubService', 'flowinglyModelUtilityService'];

  private nodeRefLetters: string;

  constructor(
    private pubsubService: Services.PubSubService,
    private flowinglyModelUtilityService: Services.FlowinglyModelUtilityService
  ) {
    this.nodeRefLetters = '';
  }

  public calculateNodesRefSequenceForModel(model: {
    nodeDataArray: IModelerNode[];
    linkDataArray: IModelerNodeLink[];
  }): void {
    this.init(model.nodeDataArray);

    const root = this.flowinglyModelUtilityService.getRootModelNode(
      model.nodeDataArray,
      model.linkDataArray
    );

    this.calculateRefSequenceDFSQueue(
      root.node,
      this.getNextRefLetters(),
      -1,
      model.nodeDataArray,
      root.links
    );

    this.pubsubService.publish('STEP_REF_SEQUENCE_CHANGED', {
      nodes: model.nodeDataArray
    });
  }

  private init(nodes: IModelerNode[]): void {
    this.nodeRefLetters = '';
    nodes.forEach((node) => {
      node.refSequence = undefined;
    });
  }

  private getNextRefLetters(): string {
    for (let i = this.nodeRefLetters.length - 1; i >= 0; i--) {
      const letter = this.nodeRefLetters[i];
      if (letter >= 'A' && letter < 'Z') {
        const newLetter = String.fromCharCode(letter.charCodeAt(0) + 1);
        this.nodeRefLetters =
          this.nodeRefLetters.substring(0, i) +
          newLetter +
          this.nodeRefLetters.substring(i + 1);
        return this.nodeRefLetters;
      }
      this.nodeRefLetters =
        this.nodeRefLetters.substring(0, i) +
        'A' +
        this.nodeRefLetters.substring(i + 1);
    }

    this.nodeRefLetters = 'A' + this.nodeRefLetters;
    return this.nodeRefLetters;
  }

  /**
   * Traverses one hop from nodeKey, returning the immediate next nodes that require
   * explicit handling: ACTIVITY/COMPONENT nodes (labelable), DIVERGE_GATEWAY and
   * EXCLUSIVE_GATEWAY nodes (not labeled, but returned so processPrimaryChain can
   * own their branch fanout and preserve shallow-before-deep letter ordering).
   * Passes transparently through CONVERGE_GATEWAY only.
   */
  private getImmediateActivityOrDivergeNodes(
    nodeKey: number,
    nodes: IModelerNode[],
    links: IModelerNodeLink[]
  ): IModelerNode[] {
    const directNext = this.flowinglyModelUtilityService.getNextNodesForNode(
      nodeKey,
      nodes,
      links
    );
    const result: IModelerNode[] = [];

    for (const node of directNext) {
      switch (node.category) {
        case NodeCategory.ACTIVITY:
        case NodeCategory.COMPONENT:
        case NodeCategory.DIVERGE_GATEWAY:
        case NodeCategory.EXCLUSIVE_GATEWAY:
          result.push(node);
          break;
        case NodeCategory.CONVERGE_GATEWAY:
          result.push(
            ...this.getImmediateActivityOrDivergeNodes(node.key, nodes, links)
          );
          break;
      }
    }

    return result;
  }

  /**
   * Labels nodes using DFS on the primary chain with a FIFO queue for secondary branches.
   *
   * Primary path: followed recursively to completion first, giving it labeling precedence.
   * The primary chain flows naturally through CONVERGE gateways, so post-CONVERGE nodes are
   * always labeled before any secondary branch is dequeued. Secondary branches that reach
   * already-labeled nodes are stopped by the node.refSequence guard.
   *
   * Secondary branches: enqueued at discovery time (letters assigned immediately), processed
   * FIFO so shallower branches are labeled before deeper ones.
   */
  private calculateRefSequenceDFSQueue(
    startNode: NodeLike,
    startRefLetter: string,
    startRefNum: number,
    nodes: IModelerNode[],
    links: IModelerNodeLink[]
  ): void {
    const queue: QueueItem[] = [];

    const processPrimaryChain = (
      node: NodeLike,
      refLetter: string,
      refNum: number
    ): void => {
      if (node.refSequence) return;

      // DIVERGE and EXCLUSIVE gateways are transparent routing nodes and are never labeled.
      // Enqueue secondary branches with new letters and follow the primary branch.
      // Handling each gateway level separately (rather than flattening) ensures shallower
      // secondaries always receive earlier letters than deeper ones.
      if (
        node.category === NodeCategory.DIVERGE_GATEWAY ||
        node.category === NodeCategory.EXCLUSIVE_GATEWAY
      ) {
        const branchNodes = this.getImmediateActivityOrDivergeNodes(
          node.key,
          nodes,
          links
        );
        branchNodes.slice(1).forEach((branch) => {
          if (!branch.refSequence) {
            queue.push({
              node: branch,
              refLetter: this.getNextRefLetters(),
              refNum: 0
            });
          }
        });
        if (branchNodes.length > 0) {
          processPrimaryChain(branchNodes[0], refLetter, refNum);
        }
        return;
      }

      node.refSequence = `${refLetter}.${refNum}`;

      const immediateNext = this.getImmediateActivityOrDivergeNodes(
        node.key,
        nodes,
        links
      );

      if (immediateNext.length === 0) return;

      const firstNext = immediateNext[0];

      // Enqueue secondary branch outputs at discovery time.
      immediateNext.slice(1).forEach((branch) => {
        if (!branch.refSequence) {
          queue.push({
            node: branch,
            refLetter: this.getNextRefLetters(),
            refNum: 0
          });
        }
      });

      processPrimaryChain(firstNext, refLetter, refNum + 1);
    };

    processPrimaryChain(startNode, startRefLetter, startRefNum);

    let queueIndex = 0;
    while (queueIndex < queue.length) {
      const { node, refLetter, refNum } = queue[queueIndex++];
      processPrimaryChain(node, refLetter, refNum);
    }
  }
}

angular
  .module('flowingly.services')
  .service('flowModelNodeSequenceService', FlowModelNodeSequenceService);

export type FlowModelNodeSequenceServiceType = InstanceType<
  typeof FlowModelNodeSequenceService
>;
